// src/resources/UserImport.js

import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Alert } from '@mui/material';
import { useNotify, useRefresh, fetchUtils } from 'react-admin';
import Papa from 'papaparse';
import { API_URL } from '../config';
import UploadFileIcon from '@mui/icons-material/UploadFile';

export const UserImportButton = () => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const notify = useNotify();
    const refresh = useRefresh();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setFile(null);
        setError('');
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setError('');
        } else {
            setFile(null);
            setError('Please select a valid CSV file.');
        }
    };

    const handleImport = () => {
        if (!file) return;

        Papa.parse(file, {
            header: true, // Uses the first row as object keys
            skipEmptyLines: true,
            complete: (results) => {
                const users = results.data;
                const token = localStorage.getItem('token');

                fetchUtils.fetchJson(`${API_URL}/admin/users/import`, {
                    method: 'POST',
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: JSON.stringify({ users }),
                })
                .then(({ json }) => {
                    notify(json.message || 'Users imported successfully!', { type: 'success' });
                    refresh();
                    handleClose();
                })
                .catch((err) => {
                    notify(err.body?.error || 'Import failed!', { type: 'warning' });
                });
            },
        });
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                label="Import"
                startIcon={<UploadFileIcon />}
            >
                Import
            </Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Import Users from CSV</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <Typography variant="body1">
                            Select a CSV file to import. The file must contain columns with headers like: `id`, `name`, `email`, `phone`, `isVerified`, `isPremium`.
                        </Typography>
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The `email` column will be used to match existing users. Passwords will not be imported for security reasons.
                        </Alert>
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ mt: 3 }}
                        >
                            {file ? file.name : 'Select File'}
                            <input
                                type="file"
                                hidden
                                accept=".csv"
                                onChange={handleFileChange}
                            />
                        </Button>
                        {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleImport} disabled={!file} variant="contained">
                        Import
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};